import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import moment from "moment";
import { useDispatch } from "react-redux";

// Components (MUI v4)
import { Grid } from "@material-ui/core";

// Icons (MUI v4)
import CheckIcon from "@material-ui/icons/Check";
import FileCopyIcon from "@material-ui/icons/FileCopy";

// Redux
import { setDataAlert } from "../../../../../../../@core/redux/actions/app/alert";

// CSS
import styles from "./../../../../assets/css/Style.module.css";
import buttonStyles from "./../../../../assets/css/Button.module.css";

// Assets
import transferGif from "images/crypto_icon/icon-animation/5-transfer_1 1.gif";
import ConfirmDialog from "views/app-mode/vertical/components/Alert/ConfirmDialog";

const BoxProcessP2P = memo(({ money, bankList, depositItems, paymentResultState, setMode, timeDepositProcess, cancelTransaction, setTimeDepositProcess, isExpired, setIsExpired }) => {
  const dispatch = useDispatch();

  const p2pResult = paymentResultState || depositItems?.payment_result || {};
  const currentLeg = p2pResult.legs?.[0] || {};
  const payTo = currentLeg.pay_to || null;
  const isMatched = !!payTo;

  // เพิ่ม State สำหรับควบคุมปุ่มยกเลิก
  const [canCancel, setCanCancel] = useState(false);
  const [cancelTimeShow, setCancelTimeShow] = useState("15:00");

  const userBank = {
    bankimg: depositItems?.customer_bankimg || bankList?.[0]?.bankimg || "",
    bankname: depositItems?.customer_bankname || bankList?.[0]?.bankname || "-",
    account: depositItems?.customer_bank_account || bankList?.[0]?.account || "-",
    name: depositItems?.customer_bank_account_name || bankList?.[0]?.name || "-"
  };

  const displayAmount = p2pResult.amount || money || 0;

  const [timeShow, setTimeShow] = useState("15:00");
  const [copied, setCopied] = useState({ ref: false, acc: false, amount: false });
  const [showCancelModal, setShowCancelModal] = useState(false);

  const intervalRef = useRef(null);

  // 1. หาค่า ID ที่ต้องการใช้ก่อน (ดึง match_id เป็นหลัก ถ้าไม่มีให้ใช้ id)
  const actualRef = p2pResult?.id || depositItems?.transaction_uuid;

  // 2. นำมาเข้าเงื่อนไขแสดงผลของ UI
  const refNumber = isMatched
    ? (actualRef || "กำลังสร้างหมายเลข...")
    : "กำลังจับคู่รายการ...";

  const handleTimeout = useCallback(() => {
    dispatch(
      setDataAlert({
        type: "warning",
        title: "แจ้งเตือน",
        text: (isMatched ? "คุณไม่โอนเงินตามเวลาที่กำหนด " : "ไม่สามารถจับคู่บัญชีได้ในขณะนี้ ") + " กรุณาทำรายการใหม่อีกครั้ง",
        action: true,
        redirect: null,
        show: true,
        buttonText: "ตกลง",
      })
    );

    if (isMatched) {
      setMode("cancel");
    }

    setTimeDepositProcess(null);
  }, [dispatch, setMode, isMatched, setTimeDepositProcess]);

  const updateCountdown = useCallback(() => {
    // ให้ความสำคัญกับ appointment_deadline ก่อนเสมอ
    const deadline = currentLeg?.appointment_deadline || timeDepositProcess;
    if (!deadline) return;

    const targetTime = moment.utc(deadline).local();
    const now = moment();
    const duration = moment.duration(targetTime.diff(now));
    const seconds = duration.asSeconds();

    if (seconds <= 0) {
      clearInterval(intervalRef.current);
      if (!isExpired) {
        setIsExpired(true);
        setTimeShow("00:00");
        handleTimeout();
        setMode("cancel");
        setTimeDepositProcess(null);
      }
    } else {
      // 1. อัปเดตเวลาถอยหลังหลัก (60 นาที)
      const m = String(Math.floor(seconds / 60)).padStart(2, "0");
      const s = String(Math.ceil(seconds % 60)).padStart(2, "0");
      setTimeShow(`${m}:${s}`);

      // 2. ลอจิกปลดล็อคปุ่มยกเลิก (ห้ามยกเลิกใน 15 นาทีแรก)
      // ตัดที่ 45 นาที = 2,700 วินาที
      if (seconds > 2700) {
        setCanCancel(false);
        const lockSeconds = seconds - 2700;
        const lockM = String(Math.floor(lockSeconds / 60)).padStart(2, "0");
        const lockS = String(Math.ceil(lockSeconds % 60)).padStart(2, "0");
        setCancelTimeShow(`${lockM}:${lockS}`);
      } else {
        setCanCancel(true);
      }
    }
  }, [timeDepositProcess, currentLeg?.appointment_deadline, isExpired, handleTimeout, setMode, setTimeDepositProcess]);

  useEffect(() => {
    updateCountdown();
    intervalRef.current = setInterval(updateCountdown, 1000);
    return () => clearInterval(intervalRef.current);
  }, [updateCountdown]);

  const handleCopy = (text, type) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(String(text)).then(() => {
        setCopied((prev) => ({ ...prev, [type]: true }));
        setTimeout(() => setCopied((prev) => ({ ...prev, [type]: false })), 2000);
      });
    } else {
      // Fallback สำหรับเบราว์เซอร์เก่า
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied((prev) => ({ ...prev, [type]: true }));
      setTimeout(() => setCopied((prev) => ({ ...prev, [type]: false })), 2000);
    }
  };

  const truncateRef = (str) => {
    if (!str) return "";
    return str.length > 23 ? str.substring(0, 23) + "..." : str;
  };

  return (
    <div className="boxPaddingBottom50">

      {/* ─── Stepper ─── */}
      <div className={styles.boxMarginFormTop}>
        <div className={styles.boxDetailTop}>
          <div className="p2pStepperWrapper">
            <div className="p2pStepItem">
              <div className={`p2pStepCircle ${isMatched ? "p2pStepCircleChecked" : "p2pStepCircleActive"}`}>
                {isMatched ? <CheckIcon style={{ fontSize: 14 }} /> : <span className="p2pStepDot" />}
              </div>
              <div className="p2pStepLabel p2pStepLabelActive">จับคู่บัญชี</div>
            </div>
            <div className={`p2pStepConnector ${isMatched ? "p2pStepConnectorActive" : ""}`} />
            <div className="p2pStepItem">
              <div className={`p2pStepCircle ${isMatched ? "p2pStepCircleActive" : "p2pStepCircleIdle"}`}>
                <span className={`p2pStepDot ${isMatched ? "" : "p2pStepDotIdle"}`} />
              </div>
              <div className={`p2pStepLabel ${isMatched ? "p2pStepLabelActive" : "p2pStepLabelIdle"}`}>โอนเงิน</div>
            </div>
            <div className="p2pStepConnector" />
            <div className="p2pStepItem">
              <div className="p2pStepCircle p2pStepCircleIdle"><span className="p2pStepDot p2pStepDotIdle" /></div>
              <div className="p2pStepLabel p2pStepLabelIdle">แนบสลิป</div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── บัญชีของลูกค้า (ใช้โอน) ─── */}
      <div className={styles.boxMarginFormTop03}>
        <div className={styles.boxDetailTop}>
          <div className={[styles.boxAccountAgentTitle, "boxPaddingBottomNone"].join(" ")}>บัญชีที่ต้องใช้โอนเงิน</div>
          <div className={[styles.boxMarginTop, "borderBgLine", "boxPaddingBottom02"].join(" ")}>
            <Grid container direction="row" spacing={2} alignItems="center">
              <Grid item xs={2} md={2}>
                <img src={userBank.bankimg} alt={userBank.bankname} className="imageSlipSize borderRadius8" />
              </Grid>
              <Grid item xs={10} md={10} className="lineHeight24">
                <div className={[styles.boxAccountAgentText, "textBoldHight"].join(" ")}>{userBank.bankname}</div>
                <div className={styles.boxAccountAgentText}>
                  <Grid container justifyContent="space-between">
                    <Grid item><span>{userBank.account}</span></Grid>
                    <Grid item><span>{userBank.name}</span></Grid>
                  </Grid>
                </div>
              </Grid>
            </Grid>
          </div>

          <div className={styles.boxMarginTop}>
            <div className="p2pWarningBanner">
              <img className="imgDepositProcess flexShrink0" src="/images/24-Alert-t.gif" alt="แจ้งเตือน" />
              <span className="p2pWarningText">ใช้บัญชีนี้ในการโอนเงินเท่านั้น!!</span>
            </div>
          </div>

          {/* ─── เลขอ้างอิง ─── */}
          <div className={styles.boxMarginTop}>
            <div className="cryptoQRAddressText borderBgLine p2pRefContainer">
              <Grid container alignItems="center" justifyContent="space-between" wrap="nowrap">
                <Grid item xs={3} className="flexStartCenter">
                  <div className="crypto-text-body-14-main flexStartCenter lineHeight1"><span className="text-main">เลขอ้างอิง :</span></div>
                </Grid>
                <Grid item xs={6} className="flexCenterCenter">
                  <div className="crypto-text-body-14-main">
                    {truncateRef(refNumber)}
                  </div>
                </Grid>
                <Grid item xs={3} className="flexEndCenter">
                  <button className={`boxDetailQRCodeScanNumberTransferCopy ${!timeDepositProcess ? '' : 'btnHover'}`} onClick={() => handleCopy(refNumber, "ref")}>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item>{copied.ref ? <CheckIcon className="iconTransactionaction iconTransactionactionRe" style={{ fontSize: 16 }} /> : <FileCopyIcon className="iconTransactionaction iconTransactionactionRe" style={{ fontSize: 16 }} />}</Grid>
                      <Grid item>{copied.ref ? "คัดลอกแล้ว" : "คัดลอก"}</Grid>
                    </Grid>
                  </button>
                </Grid>
              </Grid>
            </div>
          </div>

          {/* ─── ยอดโอน ─── */}
          <div className={styles.boxMarginTop}>
            <div className="cryptoQRAddressText borderBgLine p2pRefContainer cryptoQRAddressShadow cryptoQRAddressText02">
              <Grid container alignItems="center" justifyContent="space-between" wrap="nowrap">
                <Grid item xs={3} className="flexStartCenter">
                  <div className="crypto-text-body-14-main flexStartCenter lineHeight1"><span className="text-main">ยอดโอน :</span></div>
                </Grid>
                <Grid item xs={6} className="flexCenterCenter">
                  <span className="p2pAmountNumber"><span className="color-primary">฿{Number(displayAmount).toLocaleString()}</span></span>
                </Grid>
                <Grid item xs={3} className="flexEndCenter">
                  <button className={`boxDetailQRCodeScanNumberTransferCopy ${!timeDepositProcess ? '' : 'btnHover'}`} onClick={() => handleCopy(displayAmount, "amount")}>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item>{copied.amount ? <CheckIcon className="iconTransactionaction iconTransactionactionRe" style={{ fontSize: 16 }} /> : <FileCopyIcon className="iconTransactionaction iconTransactionactionRe" style={{ fontSize: 16 }} />}</Grid>
                      <Grid item>{copied.amount ? "คัดลอกแล้ว" : "คัดลอก"}</Grid>
                    </Grid>
                  </button>
                </Grid>
              </Grid>
            </div>
          </div>
        </div>
      </div>

      {/* ─── สถานะจับคู่ / บัญชีรับเงิน ─── */}
      <div className={styles.boxMarginFormTop03}>
        <div className={styles.boxDetailTop}>
          {!isMatched ? (
            <>
              <div className="contentBoxMarginTop p2pMatchingBox">
                <div className="center"><img src={transferGif} alt="กำลังจับคู่บัญชี" className="p2pMatchingGif" /></div>
                <div className="center contentBoxMarginTop"><div className="p2pMatchingText">กำลังจับคู่บัญชี กรุณารอสักครู่...</div></div>
              </div>
              <div className={[styles.boxMarginTop, "center"].join(" ")}>
                {/* {!canCancel ? (
                  <div className="crypto-text-body-14-main" style={{ color: "var(--color-warning)" }}>
                    คุณจะสามารถยกเลิกรายการได้ในอีก {cancelTimeShow} นาที
                  </div>
                ) : (
                  <span className="p2pCancelLink cursorPointer" onClick={() => setShowCancelModal(true)} style={{ textDecoration: "underline", color: "var(--color-danger)" }}>
                    ยกเลิกการจับคู่
                  </span>
                )} */}

                <div className="right">
                  <span className="p2pCancelLink" onClick={() => setShowCancelModal(true)}>
                    ยกเลิกการจับคู่
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="center boxPaddingBottom02">
                {!canCancel ? (
                  // ⏱️ UI ช่วง 15 นาทีแรก (ยังยกเลิกไม่ได้)
                  <div className="boxDetailAmountQrcodeNumber center marginTop0" style={{ marginTop: "0px" }}>
                    กรุณาทำรายการภายใน : <span className="boxDetailAmountQrcodeNumberText">{cancelTimeShow}</span>
                  </div>
                ) : (
                  // 🚨 UI ช่วง 45 นาทีหลัง (รอระบบตัดจบ)
                  <div className="center" style={{ flexDirection: "column", display: "flex", alignItems: "center" }}>
                    <div className="boxDetailAmountQrcodeNumber center marginTop0" style={{ marginTop: "0px" }}>
                      อยู่ในสถานะรอดำเนินการ : <span className="boxDetailAmountQrcodeNumberText">{timeShow}</span>
                    </div>
                    <div className="p2pWarningBanner p2pWarningText p2pWarningBanner02" style={{ lineHeight: "24px", padding: "6px 0px", marginTop: "8px" }}>
                      ถ้าไม่ทำรายการในเวลาที่กำหนดจะโดนระงับการใช้งานชั่วคราว
                    </div>
                  </div>
                )}
              </div>

              <div className={[styles.boxMarginTop, "boxBankTransaction", "borderBgLine"].join(" ")}>
                <div className={[styles.boxAccountAgentTitle, "boxPaddingBottomNone"].join(" ")}>โอนเข้าบัญชี</div>
                <div className={styles.boxMarginTop}>
                  <Grid container direction="row" spacing={2} alignItems="center">
                    <Grid item xs={2} md={2}>
                      <img src={payTo.bank_img || `/images/bank/${payTo.bank_code?.toLowerCase()}.png`} alt={payTo.bank_name || payTo.short_name} className="imageSlipSize borderRadius8" />
                    </Grid>
                    <Grid item xs={10} md={10} className="lineHeight24">
                      <div className={[styles.boxAccountAgentText, "textBoldHight"].join(" ")}>{payTo.bank_name || `ธนาคาร ${payTo.short_name}`}</div>
                      <div className={styles.boxAccountAgentText}>
                        <Grid container justifyContent="space-between">
                          <Grid item><span>{payTo.account_number}</span></Grid>
                          <Grid item><span>{payTo.account_name}</span></Grid>
                        </Grid>
                      </div>
                    </Grid>
                  </Grid>
                </div>
                <div className={[styles.boxMarginTop, "center"].join(" ")}>
                  <button className={[buttonStyles.btnConfirm, buttonStyles.btnCopy, "btnWidthMedium"].join(" ")} onClick={() => handleCopy(payTo.account_number, "acc")}>
                    {copied.acc ? "คัดลอกเลขบัญชีแล้ว" : "คัดลอกเลขบัญชี"}
                  </button>
                </div>
              </div>

              <div className={styles.boxMarginFormTop03}>
                <div className="center">
                  <button className={[buttonStyles.btnConfirm, buttonStyles.btnReqBank, "btnWidthFull"].join(" ")} onClick={() => setMode("slip")}>
                    ยืนยันการโอนเงิน
                  </button>
                </div>
              </div>

              {/* ปุ่มยกเลิกเมื่อจับคู่แล้ว และครบ 15 นาที */}
              {canCancel && (
                <div className={[styles.boxMarginFormTop03, "center"].join(" ")} style={{ marginTop: "16px" }}>
                  <span className="p2pCancelLink cursorPointer" onClick={() => setShowCancelModal(true)} style={{ textDecoration: "underline", color: "var(--color-danger)" }}>
                    ยกเลิกรายการ
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        show={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={() => {
          cancelTransaction();
          setShowCancelModal(false);
        }}
        title={`ยืนยันการยกเลิก${isMatched ? "รายการฝาก" : "การจับคู่"}`}
        text={<>หากยกเลิกรายการบ่อยครั้ง<br />ระบบอาจจำกัดการใช้งาน P2P ชั่วคราว</>}
        type="warning"
        cancelText="ยกเลิก"
        confirmText="ยืนยัน"
        confirmClassName="btnDanger btnHover"
      />
    </div>
  );
});

export default BoxProcessP2P;